import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, CircularProgress, Alert, Chip, Avatar,
    ToggleButton, ToggleButtonGroup, Drawer, Divider, TextField,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton, useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ReportService from '../services/report.service';
import dayjs from 'dayjs';
import { usePreferences } from '../contexts/PreferencesContext';

// Statické súbory (screenshoty) sú servírované cez rovnaký host ako API.
const MEDIA_BASE = 'https://app.effzone.com';

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0] || '?').substring(0, 2).toUpperCase();
};

const ReportManagement = ({ onReportAction }) => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [statusFilter, setStatusFilter] = useState('open');
    const [selected, setSelected] = useState(null);        // report v detail draweri
    const [resolveOpen, setResolveOpen] = useState(false); // resolve dialog
    const [resolveNote, setResolveNote] = useState('');
    const [resolving, setResolving] = useState(false);
    const [lightbox, setLightbox] = useState(null);        // full-size screenshot

    const fetchReports = useCallback(() => {
        setLoading(true);
        setError('');
        ReportService.getReports(statusFilter === 'all' ? undefined : statusFilter)
            .then((res) => setReports(res.data || []))
            .catch((err) => {
                setError(err.response?.data?.message || err.message || t('pages.reports.loadError'));
            })
            .finally(() => setLoading(false));
    }, [statusFilter, t]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleOpenResolve = () => {
        setResolveNote('');
        setResolveOpen(true);
    };

    const handleConfirmResolve = () => {
        if (!selected) return;
        setResolving(true);
        setError('');
        setSuccess('');
        ReportService.resolveReport(selected.id, resolveNote.trim())
            .then(() => {
                setSuccess(t('pages.reports.resolvedSuccess'));
                setResolveOpen(false);
                setSelected(null);
                fetchReports();
                if (onReportAction) onReportAction();
            })
            .catch((err) => {
                setError(err.response?.data?.message || t('pages.reports.resolveError'));
            })
            .finally(() => setResolving(false));
    };

    const screenshotUrl = (report) =>
        report?.screenshot_url ? `${MEDIA_BASE}${report.screenshot_url}` : null;

    const deviceRows = (info) => {
        if (!info || typeof info !== 'object') return [];
        const labels = {
            route_name: t('pages.reports.detailScreen'),
            app_version: t('pages.reports.detailAppVersion'),
            system: t('pages.reports.detailSystem'),
            device: t('pages.reports.detailDevice'),
        };
        return Object.keys(labels)
            .filter((k) => info[k])
            .map((k) => ({ label: labels[k], value: String(info[k]) }));
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto', bgcolor: 'background.default' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
                {t('pages.reports.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {t('pages.reports.description')}
            </Typography>

            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

            <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, val) => val && setStatusFilter(val)}
                size="small"
                sx={{ mb: 3 }}
            >
                <ToggleButton value="open">{t('pages.reports.filterOpen')}</ToggleButton>
                <ToggleButton value="resolved">{t('pages.reports.filterResolved')}</ToggleButton>
                <ToggleButton value="all">{t('pages.reports.filterAll')}</ToggleButton>
            </ToggleButtonGroup>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 3,
                }}
            >
                {reports.length > 0 ? reports.map((report) => (
                    <Paper
                        key={report.id}
                        onClick={() => setSelected(report)}
                        sx={{
                            p: 0, display: 'flex', flexDirection: 'column', borderRadius: 4, cursor: 'pointer',
                            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
                            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px rgba(0,0,0,0.1)' },
                            bgcolor: 'background.paper', height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2.5, pb: 1 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 44, height: 44, fontSize: '1rem', fontWeight: 'bold' }}>
                                {getInitials(report.employee_name)}
                            </Avatar>
                            <Box sx={{ ml: 1.5, minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {report.employee_name || `#${report.employee_id}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                    {report.company_name || '—'}
                                </Typography>
                            </Box>
                            <Chip
                                label={report.status === 'resolved' ? t('pages.reports.statusResolved') : t('pages.reports.statusOpen')}
                                size="small"
                                color={report.status === 'resolved' ? 'success' : 'warning'}
                                variant={report.status === 'resolved' ? 'filled' : 'outlined'}
                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                        </Box>

                        <Box sx={{ px: 2.5, py: 1, flexGrow: 1 }}>
                            <Typography variant="body2" color="text.primary" sx={{
                                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                overflow: 'hidden', mb: 1.5,
                            }}>
                                {report.description}
                            </Typography>
                        </Box>

                        <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {report.screenshot_url && <BugReportOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            <Typography variant="caption" color="text.secondary">
                                {dayjs(report.created_at).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                        </Box>
                    </Paper>
                )) : (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, gridColumn: '1 / -1' }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('pages.reports.empty')}
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Detail v pravom draweri */}
            <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)}>
                <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                            {t('pages.reports.detailTitle')}
                        </Typography>
                        <IconButton onClick={() => setSelected(null)} size="small"><CloseIcon /></IconButton>
                    </Box>

                    {selected && (
                        <Box sx={{ overflowY: 'auto', flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 40, height: 40, fontWeight: 'bold' }}>
                                    {getInitials(selected.employee_name)}
                                </Avatar>
                                <Box sx={{ ml: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selected.employee_name || `#${selected.employee_id}`}</Typography>
                                    <Typography variant="caption" color="text.secondary">{selected.company_name || '—'}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }} />
                                <Chip
                                    label={selected.status === 'resolved' ? t('pages.reports.statusResolved') : t('pages.reports.statusOpen')}
                                    size="small"
                                    color={selected.status === 'resolved' ? 'success' : 'warning'}
                                    variant={selected.status === 'resolved' ? 'filled' : 'outlined'}
                                />
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {t('pages.reports.descriptionLabel')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, mb: 2, whiteSpace: 'pre-wrap' }}>
                                {selected.description}
                            </Typography>

                            {screenshotUrl(selected) && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                        {t('pages.reports.screenshotLabel')}
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={screenshotUrl(selected)}
                                        alt="screenshot"
                                        onClick={() => setLightbox(screenshotUrl(selected))}
                                        sx={{
                                            maxWidth: 200, maxHeight: 360, borderRadius: 2, cursor: 'zoom-in',
                                            border: `1px solid ${theme.palette.divider}`, display: 'block',
                                        }}
                                    />
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PhoneIphoneIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {t('pages.reports.diagnostics')}
                                </Typography>
                            </Box>
                            {deviceRows(selected.device_info).map((row) => (
                                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{row.label}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right' }}>{row.value}</Typography>
                                </Box>
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">{t('pages.reports.createdAt')}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>{dayjs(selected.created_at).format('DD.MM.YYYY HH:mm')}</Typography>
                            </Box>

                            {selected.status === 'resolved' && (
                                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                                        {t('pages.reports.resolvedAtLabel')}: {selected.resolved_at ? dayjs(selected.resolved_at).format('DD.MM.YYYY HH:mm') : '—'}
                                    </Typography>
                                    {selected.resolved_note && (
                                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>"{selected.resolved_note}"</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {selected && selected.status !== 'resolved' && (
                        <Box sx={{ pt: 2 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={handleOpenResolve}
                                sx={{ borderRadius: '10px', boxShadow: 'none', color: '#fff', '&:hover': { boxShadow: 'none' } }}
                            >
                                {t('pages.reports.markResolved')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Drawer>

            {/* Resolve dialog s voliteľnou poznámkou */}
            <Dialog open={resolveOpen} onClose={() => !resolving && setResolveOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('pages.reports.resolveDialogTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t('pages.reports.resolveDialogText')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label={t('pages.reports.resolveNoteLabel')}
                        placeholder={t('pages.reports.resolveNotePlaceholder')}
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveOpen(false)} disabled={resolving}>{t('common.cancel') || 'Zrušiť'}</Button>
                    <Button onClick={handleConfirmResolve} color="success" variant="contained" disabled={resolving} sx={{ color: '#fff' }}>
                        {resolving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t('pages.reports.markResolved')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lightbox pre screenshot */}
            <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="md">
                <Box
                    component="img"
                    src={lightbox || ''}
                    alt="screenshot"
                    sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
                    onClick={() => setLightbox(null)}
                />
            </Dialog>
        </Box>
    );
};

export default ReportManagement;
